import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import {
  ContentControlledEditingDirectiveViewModel,
  SegmentExtenderKeyframe,
} from 'app/js/src/domain/content-control/editing-directive';
import { ContentControlledMediaTypeEnum } from 'app/js/src/domain/content-control/enums';

@Component({
  selector: "evrideo-content-control-studio-timeline-track-segment",
  templateUrl: "./content-control-studio-timeline-track-segment.component.html",
  styleUrls: ["./content-control-studio-timeline-track-segment.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentControlStudioTimelineTrackSegmentComponent {
  @Input() segment: ContentControlledEditingDirectiveViewModel;
  @Input("width") segmentWidth: number;
  @Input("keyframes") keyframes: Array<SegmentExtenderKeyframe> = [];
  @Output("on-keyframe-click") onKeyframeSelect = new EventEmitter<object>();

  @ViewChild("svg") svg: ElementRef<SVGElement>;

  // Reuse a single canvas/context for text measurement
  private static canvas: HTMLCanvasElement = document.createElement("canvas");
  private static ctx: CanvasRenderingContext2D =
    ContentControlStudioTimelineTrackSegmentComponent.canvas.getContext("2d");

  ngAfterViewInit() {
    this.applyStyles();
  }
  ngOnChanges() {
    this.applyStyles();
  }

  private clearSvg() {
    if (this.svg) {
      // Fastest way to clear SVG contents
      this.svg.nativeElement.innerHTML = "";
    }
  }

  private getFillColor(): string {
    if (!this.segment) return "#CFD0D1";
    if (this.segment.mediaType === ContentControlledMediaTypeEnum.None) {
      return "#CFD0D1";
    }
    return this.segment.isApproved || this.segment.isMitigated
      ? "#01b25d"
      : "#ef5d5d";
  }

  hideText() {
    return (
      this.svg &&
      this.svg.nativeElement.parentElement.style.height !== "100%"
    );
  }

  applyStyles() {
    if (!this.svg || !this.segmentWidth) return;

    this.clearSvg(); // ✅ clear old nodes before redrawing

    const svgEl = this.svg.nativeElement;
    const color = this.getFillColor();
    const sw = Math.ceil(Math.abs(this.segmentWidth));
    svgEl.setAttribute("width", sw.toString());

    if (this.keyframes.length < 2) {
      const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
      rect.setAttribute("x", "0");
      rect.setAttribute("y", "0");
      rect.setAttribute("width", `${sw}`);
      rect.setAttribute("height", "100%");
      rect.setAttribute("fill", color);
      svgEl.appendChild(rect);
    } else {
      let newX = 0;
      for (const kf of this.keyframes) {
        const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        rect.setAttribute("x", `${newX}`);
        rect.setAttribute("y", "0");
        rect.setAttribute("width", `${kf.width}`);
        rect.setAttribute("height", "100%");
        rect.setAttribute("fill", kf.isKeyframe ? "#A2E0FD" : color);

        if (kf.isKeyframe) {
          rect.classList.add("keyframe");
          // Instead of many listeners, use one & delegate:
          rect.dataset.time = String(kf.timeInMs);
        }

        svgEl.appendChild(rect);
        newX += kf.width;
      }

      // ✅ delegate click handling (single listener)
      svgEl.onclick = (e: MouseEvent) => {
        const target = e.target as SVGRectElement;
        if (target && target.classList.contains("keyframe")) {
          const t = target.dataset.time;
          if (t) {
            this.onKeyframeSelect.emit({
              segmentId: this.segment.id,
              timeInMs: +t,
            });
          }
        }
      };
    }

    // Handle title drawing
    if (this.segment?.title) {
      let finalText = this.segment.title;
      let x = 10;
      const fontSize = 14;
      const font = `Arial ${fontSize}px`;
      ContentControlStudioTimelineTrackSegmentComponent.ctx.font = font;
      const textWidth =
        ContentControlStudioTimelineTrackSegmentComponent.ctx.measureText(
          finalText,
        ).width + x * 2;

      if (textWidth > this.segmentWidth) {
        if (this.segmentWidth > 43.334) {
          finalText = "...";
          x = 5;
        } else {
          finalText = "";
        }
      }

      if (finalText && !this.hideText()) {
        const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
        text.setAttribute("x", `${x}`);
        text.setAttribute("y", "18");
        text.setAttribute("font-family", "Arial");
        text.setAttribute("font-size", `${fontSize}`);
        text.setAttribute("fill", "black");
        text.textContent = finalText;
        svgEl.appendChild(text);
      }
    }
  }
}
